import React, { useEffect, useState } from 'react';
import { FamilyTreeNode } from '../../types/familyTree';
import { displayRelationLabel, melioRecordText, personalNotesText, treeNodeTitle } from '../../utils/familyTreeUtils';

interface FamilyTreeMemberModalProps {
  node: FamilyTreeNode;
  melioNotesUnlocked: boolean;
  canEditPersonalNotes?: boolean;
  onClose: () => void;
  onPersonalNotesChange?: (nodeId: string, notes: string) => void;
}

export default function FamilyTreeMemberModal({
  node,
  melioNotesUnlocked,
  canEditPersonalNotes = false,
  onClose,
  onPersonalNotesChange,
}: FamilyTreeMemberModalProps) {
  const [draftNotes, setDraftNotes] = useState(personalNotesText(node));
  const title = treeNodeTitle(node);
  const subtitle = node.relationKey === 'self' ? displayRelationLabel(node) : null;
  const melioText = melioRecordText(node);
  const conditions = node.conditions || [];

  useEffect(() => {
    setDraftNotes(personalNotesText(node));
  }, [node]);

  const commitNotes = () => {
    if (!canEditPersonalNotes || !onPersonalNotesChange) return;
    if (draftNotes !== personalNotesText(node)) {
      onPersonalNotesChange(node.id, draftNotes);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="family-tree-member-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(48, 46, 40, 0.45)' }}
      onClick={onClose}
    >
      <div
        className="rounded-3xl border w-full max-w-4xl overflow-hidden"
        style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="flex items-start justify-between gap-4 px-6 py-5 border-b"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: '#F7F6F2' }}
        >
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] mb-1" style={{ color: '#5C1A1C' }}>
              Family member
            </p>
            <h2
              id="family-tree-member-title"
              className="text-2xl font-normal"
              style={{ fontFamily: "var(--melio-quote)", color: '#3D0B0D' }}
            >
              {title}
            </h2>
            {subtitle && subtitle !== title && (
              <p className="text-sm mt-1" style={{ color: 'var(--melio-text-muted)' }}>
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded text-sm font-medium border transition-opacity hover:opacity-90"
            style={{ borderColor: 'var(--melio-border)', color: '#3D0B0D', backgroundColor: 'white' }}
          >
            Close
          </button>
        </div>

        <div className="grid md:grid-cols-2">
          <section className="p-6 md:border-r" style={{ borderColor: 'var(--melio-border)' }}>
            <p className="text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: '#6B6862' }}>
              Personal notes
            </p>
            <p className="text-xs mb-4 leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
              What you recorded during intake — you can update these any time editing is open.
            </p>

            {conditions.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium mb-2" style={{ color: '#3D0B0D' }}>
                  Conditions on record
                </p>
                <ul className="space-y-2">
                  {conditions.map((condition, index) => (
                    <li
                      key={`${node.id}-condition-${index}`}
                      className="rounded-xl px-3 py-2 text-sm"
                      style={{ backgroundColor: 'rgba(212, 175, 55, 0.14)', color: '#3D0B0D' }}
                    >
                      {condition.label}
                      {condition.ageAtDiagnosis ? ` · diagnosed at ${condition.ageAtDiagnosis}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {canEditPersonalNotes && onPersonalNotesChange ? (
              <textarea
                value={draftNotes}
                onChange={(event) => setDraftNotes(event.target.value)}
                onBlur={commitNotes}
                rows={6}
                placeholder="Add anything your clinician should know — ages, details, context…"
                className="w-full rounded-xl border px-3 py-2 text-sm leading-relaxed resize-y"
                style={{ borderColor: 'var(--melio-border)', color: '#3D0B0D' }}
              />
            ) : (
              <div
                className="rounded-xl border px-3 py-3 text-sm leading-relaxed min-h-[7rem]"
                style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-text-muted)', backgroundColor: 'var(--b-bone)' }}
              >
                {personalNotesText(node) || 'No personal notes recorded yet.'}
              </div>
            )}
          </section>

          <section className="p-6 relative">
            <p className="text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: '#6B6862' }}>
              Melio record
            </p>
            <p className="text-xs mb-4 leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
              Clinician-reviewed notes for your care team. These feed clinical intelligence and cannot be edited here.
            </p>

            <div className="relative min-h-[10rem]">
              <div
                className="rounded-xl border px-3 py-3 text-sm leading-relaxed"
                style={{
                  borderColor: 'var(--melio-border)',
                  color: '#3D0B0D',
                  backgroundColor: 'var(--b-bone)',
                  filter: melioNotesUnlocked ? 'none' : 'blur(5px)',
                  userSelect: melioNotesUnlocked ? 'text' : 'none',
                }}
              >
                {melioText || 'No clinician notes recorded for this relative yet.'}
              </div>

              {!melioNotesUnlocked && (
                <div
                  className="absolute inset-0 flex items-center justify-center rounded-xl px-4 text-center"
                  style={{ backgroundColor: 'rgba(247, 246, 242, 0.72)' }}
                >
                  <div>
                    <p
                      className="text-lg font-normal mb-2"
                      style={{ fontFamily: "var(--melio-quote)", color: '#3D0B0D' }}
                    >
                      Waiting on your clinician&apos;s notes
                    </p>
                    <p className="text-sm leading-relaxed max-w-xs mx-auto" style={{ color: 'var(--melio-text-muted)' }}>
                      After your clinical history review, Melio notes will appear here for each family member.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
